import type CardClient from '@atlaskit/link-provider/client';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import type { ReadContentCommandResult } from '@atlassian/rovo-content-bridge-api-commands/commands/read-content-command';

import type {
	Experience,
	ExperienceManifest,
	ExperienceManifestId,
	NativeEmbedClientId,
} from '../../common/types';
import type { Surface } from '../../common/types/surface/types';
import type { URLMetaData } from '../../common/types/url/types';
import {
	type ExperienceManifestRegistry,
	getExperienceManifestRegistry,
} from '../experience-manifest-registry/main';
import { getSurfaceService, type SurfaceService } from '../surface-service/main';
import type { SetupConfig } from './types';

/**
 * Matches Confluence short-link URLs of the form /wiki/x/<token>.
 * These URLs do not map to a specific experience until resolved via ORS.
 */
const CONFLUENCE_SHORT_LINK_PATTERN = /\/wiki\/x\/[^/]+/;

let nativeEmbedFacade: NativeEmbedFacade | undefined;

export function getNativeEmbedFacade(): NativeEmbedFacade {
	if (!nativeEmbedFacade) {
		nativeEmbedFacade = new NativeEmbedFacade(getExperienceManifestRegistry(), getSurfaceService());
	}

	return nativeEmbedFacade;
}

export type ExperienceFacadeResult = {
	experience: Experience;
	manifest: ExperienceManifest;
};

export class NativeEmbedFacade {
	private experienceManifestRegistry: ExperienceManifestRegistry;
	private surfaceService: SurfaceService;
	private cardClient: CardClient | undefined;
	private isSetUp = false;

	constructor(
		experienceManifestRegistry: ExperienceManifestRegistry,
		surfaceService: SurfaceService,
	) {
		this.experienceManifestRegistry = experienceManifestRegistry;
		this.surfaceService = surfaceService;
	}

	/**
	 * Registers an experience manifest so it can be matched by URL via {@link resolveManifestForUrl}.
	 */
	registerExperienceManifest(manifest: ExperienceManifest): void {
		this.experienceManifestRegistry.registerExperienceManifest(manifest);
	}

	/**
	 * One-shot convenience method that registers experience manifests and
	 * optionally configures the host surface in a single call.
	 *
	 * Prefer `setup()` over calling `registerExperienceManifest` /
	 * `setSurface` individually — it makes the configuration declarative,
	 * validates inputs eagerly, and guards against accidental double-setup.
	 *
	 * The low-level methods remain available for advanced use cases
	 * (conditional registration, dynamic surface changes, etc.).
	 *
	 * @param config - The setup configuration.
	 * @param config.experiences - Non-empty array of experience manifests to register.
	 * @param config.surface - Optional surface configuration.
	 *
	 * Logs a warning and returns early if `setup()` has already been called.
	 *
	 * @example
	 * ```ts
	 * import { getNativeEmbedFacade } from '@atlassian/native-embeds-core';
	 *
	 * getNativeEmbedFacade().setup({
	 *   experiences: [WhiteboardExperienceManifest, DBExperienceManifest],
	 * });
	 * ```
	 */
	setup(config: SetupConfig): void {
		if (this.isSetUp) {
			// eslint-disable-next-line no-console
			console.warn(
				'NativeEmbedFacade.setup() has already been called. ' +
					'This call will be ignored. ' +
					'Use the low-level registerExperienceManifest() and setSurface() methods ' +
					'if you need to modify configuration after initial setup.',
			);
			return;
		}

		for (const manifest of config.experiences) {
			this.registerExperienceManifest(manifest);
		}

		if (config.surface) {
			this.setSurface(config.surface);
		}

		if (config.cardClient) {
			this.cardClient = config.cardClient;
		}

		this.isSetUp = true;
	}

	/**
	 * Sets the {@link CardClient} instance used for short-link URL resolution.
	 *
	 * This is useful when the `CardClient` is not available at the time `setup()`
	 * is called (e.g. when the facade is initialised at module-load time before
	 * the card provider is ready). Calling this after `setup()` replaces any
	 * previously configured client.
	 *
	 * @param cardClient - The CardClient instance to use for ORS requests.
	 */
	setCardClient(cardClient: CardClient): void {
		this.cardClient = cardClient;
	}

	/**
	 * Synchronous function that verifies if the URL matches any registered experience, and returns the experience ID if it does.
	 * @param url - The URL to get the experience from.
	 * @returns
	 */
	getExperienceIdFromUrl(url: string): ExperienceManifestId | undefined {
		return this.experienceManifestRegistry.getExperienceIdFromUrl(url);
	}

	/**
	 * Async variant of {@link getExperienceIdFromUrl} that handles Confluence
	 * short-link URLs (e.g. `/wiki/x/<token>`).
	 *
	 * When the URL is a Confluence short link and a `cardClient` has been
	 * configured (via `setup({ cardClient })` or `setCardClient()`), this method
	 * resolves the short link to its canonical URL via the Object Resolver
	 * Service and then matches the expanded URL against registered experience
	 * manifests.
	 *
	 * Falls back to the synchronous {@link getExperienceIdFromUrl} for all
	 * non-short-link URLs, or when no `cardClient` is available.
	 *
	 * @param url - The URL to resolve and match.
	 * @returns The matched experience manifest ID, or `undefined` if no match.
	 */
	async getExperienceIdFromUrlAsync(url: string): Promise<ExperienceManifestId | undefined> {
		// Fast path: direct synchronous match (works for all non-short-link URLs)
		const syncResult = this.experienceManifestRegistry.getExperienceIdFromUrl(url);
		if (syncResult) {
			return syncResult;
		}

		// Expand Confluence short links via ORS when a cardClient is available
		const expandedUrl = await this.expandShortLink(url);
		if (!expandedUrl) {
			return undefined;
		}

		return this.experienceManifestRegistry.getExperienceIdFromUrl(expandedUrl);
	}

	/**
	 * If `url` is a Confluence short link and a `cardClient` is configured,
	 * resolves it to its canonical expanded URL via the Object Resolver Service.
	 *
	 * Returns `undefined` when:
	 * - the feature gate `platform_native_embeds_rollout_non_maui_experience` is off,
	 * - the URL is not a short link,
	 * - no `cardClient` is available, or
	 * - resolution fails or returns the same URL.
	 */
	async expandShortLink(url: string): Promise<string | undefined> {
		if (!fg('platform_native_embeds_rollout_non_maui_experience')) {
			return undefined;
		}

		const isShortLink = CONFLUENCE_SHORT_LINK_PATTERN.test(url);
		if (!isShortLink || !this.cardClient) {
			return undefined;
		}

		try {
			const response = await this.cardClient.fetchData(url);
			const data = response?.data;
			if (!data || typeof data !== 'object') {
				return undefined;
			}
			const resolvedUrl = (data as unknown as Record<string, unknown>)['url'];

			if (typeof resolvedUrl === 'string' && resolvedUrl !== url) {
				return resolvedUrl;
			}
			// url can also be an object with an @id (JsonLd Link type)
			if (resolvedUrl && typeof resolvedUrl === 'object') {
				const id = (resolvedUrl as unknown as Record<string, unknown>)['@id'];
				if (typeof id === 'string' && id !== url) {
					return id;
				}
			}
		} catch {
			// Short-link expansion is best-effort; fall through to return undefined.
		}

		return undefined;
	}

	/**
	 * Synchronous function that returns the manifest for a URL if it matches any registered experience.
	 * Uses `isUrlMatch` to find the matching manifest.
	 * @param url - The URL to get the manifest for.
	 * @returns The matching manifest, or undefined if no match is found.
	 */
	getManifestByUrl(url: string): ExperienceManifest | undefined {
		return this.experienceManifestRegistry.getManifestByUrl(url);
	}

	/**
	 * Synchronous function that returns the manifest for a given experience ID.
	 * @param id - The experience manifest ID to look up.
	 * @returns The matching manifest, or undefined if no match is found.
	 */
	getManifestById(id: string): ExperienceManifest | undefined {
		return this.experienceManifestRegistry.getManifestById(id);
	}

	/**
	 * Returns a list of all registered experience IDs.
	 */
	getAllExperienceIds(): ExperienceManifestId[] {
		return this.experienceManifestRegistry.getAllExperienceIds();
	}

	/**
	 * Register or replace the active host surface.
	 *
	 * The surface provides capabilities that differ per host (editor, renderer,
	 * etc.). The {@link Surface} interface will grow as new surface concerns
	 * are identified.
	 *
	 * - Replaces any previously registered surface (last-write-wins).
	 * - Pass `null` to clear the surface (e.g. on unmount).
	 *
	 * @param surface - The surface configuration, or `null` to clear.
	 */
	setSurface(surface: Surface | null): void {
		this.surfaceService.setSurface(surface);
	}

	/**
	 * Finds the matching manifest for a URL and creates an experience.
	 * Title metadata will be resolved later by Smart Card.
	 *
	 * When the URL is a Confluence short link (e.g. `/wiki/x/<token>`) and a
	 * `cardClient` has been configured, the short link is expanded to its
	 * canonical URL via the Object Resolver Service before manifest matching.
	 * The experience is created with the expanded URL.
	 */
	async createExperienceForUrl(url: string): Promise<ExperienceFacadeResult | undefined> {
		// Reject non-URL strings early. macroParams.url may contain prose text
		// with an embedded link rather than a bare URL (e.g. after a bad paste).
		try {
			new URL(url);
		} catch {
			return undefined;
		}

		const resolvedUrl = (await this.expandShortLink(url)) ?? url;
		const manifest = this.experienceManifestRegistry.getManifestByUrl(resolvedUrl);
		if (!manifest) {
			return undefined;
		}
		const urlMeta: URLMetaData = { url: resolvedUrl, title: '' };
		// Lazy import — ExperienceService transitively pulls in
		// rovo-content-bridge-api which uses uuid/randomBytes. Keeping it out of
		// the static import graph prevents the SSR pre-compile crash.
		const { getExperienceService } = await import('../experience-service/main');
		const experience = getExperienceService().registerExperience({ manifest, urlMeta });
		return {
			experience,
			manifest,
		};
	}

	/**
	 * Unregister an experience and dispose its event listeners.
	 *
	 * Call when the embed is removed from the host surface (e.g. editor node
	 * deleted, component unmount). This prevents memory leaks from stale
	 * listener references.
	 *
	 * Safe to call with an ID that was never registered — it's a no-op.
	 */
	async destroyExperience(experienceId: NativeEmbedClientId): Promise<void> {
		const { getExperienceService } = await import('../experience-service/main');
		getExperienceService().unregisterExperience(experienceId);
	}

	/** Notify the child embed that its selection state changed. */
	async sendSelectedChange(experienceId: NativeEmbedClientId, selected: boolean): Promise<void> {
		const { getExperienceService } = await import('../experience-service/main');
		getExperienceService().sendSelectedChange(experienceId, selected);
	}

	/** Notify the child embed that the embedded content should be read. */
	async sendReadContent(
		experienceId: NativeEmbedClientId,
	): Promise<ReadContentCommandResult | undefined> {
		if (!fg('maui_remixable_page_native_embeds')) {
			return undefined;
		}
		const { getExperienceService } = await import('../experience-service/main');
		return getExperienceService().sendReadContent(experienceId);
	}

	/** Register `aliasId` as a secondary ID for `experienceId`. Overwrites any previous mapping for `aliasId`. */
	async addAlias(aliasId: NativeEmbedClientId, experienceId: NativeEmbedClientId): Promise<void> {
		const { getExperienceService } = await import('../experience-service/main');
		getExperienceService().addAlias(aliasId, experienceId);
	}
}
